import Loading from "components/Loading";
import React, { useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import COLOR from "utils/constants/color";
import { useLocation } from "react-router-dom";

const BookImage = ({
  src,
  imageCache,
  className = "",
  alt = "Book page",
  onLoad,
  onError,
}) => {
  const location = useLocation();
  const [imageLoadError, setImageLoadError] = useState(false);

  // Check if image is cached (imageCache is a Map)
  const isCached = useMemo(() => {
    return src && imageCache && imageCache.has && imageCache.has(src);
  }, [src, imageCache]);

  // For this implementation, we'll assume if it's not cached, it might be loading
  // You can enhance this by adding loading state tracking if needed
  const isLoading = useMemo(() => {
    return src && !isCached;
  }, [src, isCached]);

  const handleImageLoad = useCallback(() => {
    setImageLoadError(false);
    if (onLoad) onLoad();
  }, [onLoad]);

  const handleImageError = useCallback(() => {
    setImageLoadError(true);
    if (onError) onError();
  }, [onError]);

  const getCachedImageSrc = useMemo(() => {
    if (!src || !isCached || !imageCache) return src;
    
    const cachedImage = imageCache.get(src);
    if (cachedImage && cachedImage.src) {
      return cachedImage.src;
    }
    return src;
  }, [src, isCached, imageCache]);

  if (imageLoadError) {
    return (
      <ImageContainer>
        <ErrorContainer>
          <ErrorText>Failed to load image</ErrorText>
        </ErrorContainer>
      </ImageContainer>
    );
  }

  return (
    <ImageContainer>
      {isLoading && !isCached && (
        <LoadingContainer>
          <LoadingText>
            <Loading size={18} />
          </LoadingText>
        </LoadingContainer>
      )}
      <StyledImage
        src={getCachedImageSrc}
        alt={alt}
        onLoad={handleImageLoad}
        onError={handleImageError}
        isLoading={isLoading}
        isCached={isCached}
        className={className}
        isResource={location.pathname.includes("/video") ? true : false}
      />
    </ImageContainer>
  );
};

export default BookImage;

// Additional styled components for error handling
const ErrorContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  background-color: ${COLOR.white};
  border: 2px dashed ${COLOR.black200};
  z-index: 1;
`;

const ErrorText = styled.div`
  font-size: 14px;
  color: ${COLOR.black400};
  text-align: center;
`;

// Styled Components
const ImageContainer = styled.div`
  position: relative;
  height: 97%;
  background-color: ${COLOR.white};
  width: 100%;
  min-height: 600px;
  @media (min-width: 769px) and (max-width: 1024px) {
    /* Tablet and smaller screen styles */
    min-height: 400px; 
  }
  @media (max-width: 768px) {
    /* Tablet and smaller screen styles */
    min-height: 300px; 
  }
    @media (max-width: 500px) {
    /* Tablet and smaller screen styles */
    min-height: 100px; 
  }
`;

const StyledImage = styled.img`
  background-color: ${COLOR.white};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23);
  object-fit: ${(props) => props?.isResource ? "contain" : "fill"};
  height: 100%;
  width: 100%;
  display: ${(props) =>
    props.isLoading && !props.isCached ? "none" : "block"};
  opacity: ${(props) => (props.isCached ? 1 : props.isLoading ? 0 : 1)};
  transition: opacity 0.2s ease-in-out;

  @media print {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }
`;

const LoadingContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  background-color: ${COLOR.white};
  z-index: 1;
`;

const LoadingText = styled.div`
  font-size: 16px;
  color: ${COLOR.black600};
`;